import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Plus, Edit, Trash2, Save, Building2, Users, Upload, ChevronDown, Check,
  DollarSign, FileText, AlignLeft, Layers, Building, Truck, ClipboardList, Target, MapPin,
  Tag, Package, BookOpen, Hash, UtensilsCrossed, FileSignature, Factory, Boxes, BarChart3 } from 'lucide-react';
import { globalConfirm } from '../../../components/ConfirmDialog';
import { AddressLookup, PostcodeLookup } from '../../Suppliers/components/AddressLookup';

const COUNTRIES = ['United States','United Kingdom','Canada','Australia','Germany','France','Ireland','Netherlands','Singapore','India','Other'];
const EMPTY = { name: '', code: '', address: '', city: '', state: '', zip_postal: '', country: 'United States', phone: '', email: '', active: true };

// Module definitions
const SEPARATE_MODULES = [
  { id: 'currency', name: 'Currency', icon: DollarSign },
  { id: 'invoice_point', name: 'Invoice Point', icon: FileText },
  { id: 'standard_text', name: 'Standard Text', icon: AlignLeft },
  { id: 'account', name: 'Account', icon: Layers },
  { id: 'department_code', name: 'Department Code', icon: Building },
  { id: 'delivery', name: 'Delivery', icon: Truck },
  { id: 'order_type', name: 'Order Type', icon: ClipboardList },
  { id: 'cost_centre', name: 'Cost Centre', icon: Target },
];

const INTERLINKED_MODULES = [
  { id: 'category', name: 'Category', icon: Tag },
  { id: 'items', name: 'Items', icon: Package },
  { id: 'supplier', name: 'Supplier', icon: Factory },
  { id: 'contracts', name: 'Contracts', icon: FileSignature },
  { id: 'requisitions', name: 'Requisitions', icon: ClipboardList },
  { id: 'orders', name: 'Purchase Orders', icon: FileText },
  { id: 'gl', name: 'GL', icon: BookOpen },
  { id: 'nominal_code', name: 'Nominal Code', icon: Hash },
  { id: 'recipe', name: 'Recipe', icon: UtensilsCrossed },
  { id: 'property_code', name: 'Property Code', icon: Building2 },
  { id: 'inventory', name: 'Inventory', icon: Boxes },
  { id: 'invoices', name: 'Invoices (future)', icon: BarChart3 },
  { id: 'rfq', name: 'RFQ/RFP (future)', icon: Target },
];

export function SitesPanel() {
  const [enabled, setEnabled] = useState(false);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('function');
  
  // Site Function settings
  const [siteName, setSiteName] = useState('Site');
  const [sequentialNumbering, setSequentialNumbering] = useState(false);
  
  // Module settings
  const [separateModules, setSeparateModules] = useState({});
  const [interlinkedEnabled, setInterlinkedEnabled] = useState(false);
  const [interlinkedModules, setInterlinkedModules] = useState({});
  
  // Site Master state
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [siteUsers, setSiteUsers] = useState({});
  const [expandedSite, setExpandedSite] = useState(null);

  const load = useCallback(async () => {
    try {
      const [statusRes, sitesRes] = await Promise.all([
        api.get('/api/sites/module/status'),
        api.get('/api/sites'),
      ]);
      const status = statusRes.data;
      setEnabled(status.enabled);
      setSiteName(status.site_name || 'Site');
      setSequentialNumbering(status.sequential_numbering || false);
      setSeparateModules(status.separate_modules || {});
      setInterlinkedEnabled(status.interlinked_enabled || false);
      setInterlinkedModules(status.interlinked_modules || {});
      setSites(sitesRes.data.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/sites/module/settings', {
        enabled,
        site_name: siteName,
        sequential_numbering: sequentialNumbering,
        separate_modules: separateModules,
        interlinked_enabled: interlinkedEnabled,
        interlinked_modules: interlinkedModules,
      });
      toast.success('Settings saved');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const toggleSeparateModule = (moduleId) => {
    setSeparateModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const toggleInterlinkedModule = (moduleId) => {
    setInterlinkedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const loadSiteUsers = async (siteId) => {
    try {
      const r = await api.get(`/api/sites/${siteId}/users`);
      setSiteUsers(prev => ({ ...prev, [siteId]: r.data }));
    } catch {}
  };

  const toggleExpand = (siteId) => {
    if (expandedSite === siteId) { setExpandedSite(null); return; }
    setExpandedSite(siteId);
    if (!siteUsers[siteId]) loadSiteUsers(siteId);
  };

  const openCreate = () => { setForm(EMPTY); setIsEdit(false); setModalOpen(true); };
  const openEdit = (site) => {
    setForm({ ...EMPTY, ...site });
    setIsEdit(true);
    setModalOpen(true);
    if (!siteUsers[site.site_id]) loadSiteUsers(site.site_id);
  };

  const saveSite = async () => {
    if (!form.name?.trim()) { toast.error('Site name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/sites/${form.site_id}`, form);
        toast.success('Site updated');
        setModalOpen(false);
      } else {
        const r = await api.post('/api/sites', form);
        toast.success('Site created');
        setForm(prev => ({ ...prev, site_id: r.data.site_id }));
        setIsEdit(true);
      }
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
    try { await load(); } catch {}
  };

  const deleteSite = async (site) => {
    if (!await globalConfirm(`Delete site "${site.name}"? This will remove site access from all users.`, { title: 'Delete Site', destructive: true })) return;
    try {
      await api.delete(`/api/sites/${site.site_id}`);
      toast.success('Site deleted');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-5xl" data-testid="sites-panel">
      <h1 className="text-3xl font-bold text-[#1e3a5f] mb-1">Sites</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
        <TabsList className="mb-6">
          <TabsTrigger value="function">Site Function</TabsTrigger>
          <TabsTrigger value="modules">Site For Modules</TabsTrigger>
          <TabsTrigger value="master">{siteName} Master</TabsTrigger>
        </TabsList>

        {/* TAB 1: Site Function */}
        <TabsContent value="function" className="space-y-6">
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">Site Function</h2>
          </div>
          
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p className="italic">
              Sites is a feature that allows you to deploy the system on a per company site basis where your Users can be linked to one or more sites.
            </p>
            <p className="italic">
              Users will not be able to see Data Files, Account Codes, Requests, Orders, RFQ's or Recipes on a site that they are not linked to.
            </p>
          </div>

          <div className="space-y-2 text-sm text-gray-700 mt-6">
            <p>You can set Sites ON or OFF by checking the 'Activate Site Function' checkbox.</p>
            <p>Once Sites is set to ON, you will be logged out automatically. After logging back you will see a new option under User Maintenance &gt; Site Function &gt; Site Master&gt;</p>
            <p>You can add new Sites in Site Master and add users to those Sites.</p>
            <p>You can also Rename 'Sites' to be whatever you would like it to be called.</p>
          </div>

          <div className="space-y-8 mt-8">
            {/* Activate Site Function */}
            <div className="flex items-center gap-8">
              <Label className="w-48 text-sm font-medium text-gray-700">Activate Site Function</Label>
              <div className="flex items-center">
                <div 
                  className={`relative w-16 h-8 rounded-full cursor-pointer transition-colors ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  onClick={() => setEnabled(!enabled)}
                  data-testid="sites-activate-toggle"
                >
                  <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform flex items-center justify-center text-xs font-bold ${enabled ? 'translate-x-9 text-green-600' : 'translate-x-1 text-gray-400'}`}>
                    {enabled ? 'Yes' : ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Site Name */}
            <div className="flex items-center gap-8">
              <Label className="w-48 text-sm font-medium text-gray-700">Site Name</Label>
              <div className="relative">
                <Input 
                  value={siteName} 
                  onChange={e => setSiteName(e.target.value)} 
                  className="w-64 pr-10"
                  placeholder="Company Site"
                  data-testid="site-name-input"
                />
                <Edit className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500" />
              </div>
            </div>

            {/* Sequential Number */}
            <div className="flex items-start gap-8">
              <Label className="w-48 text-sm font-medium text-gray-700 pt-1">Sequential Number</Label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div 
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!sequentialNumbering ? 'border-[#1e3a5f] bg-[#1e3a5f]' : 'border-gray-300'}`}
                    onClick={() => setSequentialNumbering(false)}
                  >
                    {!sequentialNumbering && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                  <span className={`text-sm ${!sequentialNumbering ? 'text-[#1e3a5f] font-medium' : 'text-gray-600'}`}>No</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div 
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${sequentialNumbering ? 'border-[#1e3a5f] bg-[#1e3a5f]' : 'border-gray-300'}`}
                    onClick={() => setSequentialNumbering(true)}
                  >
                    {sequentialNumbering && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                  <span className={`text-sm ${sequentialNumbering ? 'text-[#1e3a5f] font-medium' : 'text-gray-600'}`}>Yes</span>
                </label>
                <p className="text-xs text-gray-500 mt-2 ml-8">
                  Select 'Yes' if you want Orders/Request/RFQ number to be sequential for each site, e.g. Orders for each site will start with number 000001.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6 mt-8 space-y-1 text-sm text-gray-600">
            <p><strong>Note:</strong> Activate/Deactivate the Site Function or changing the Site Name will force you to login again to the system.</p>
            <p>All users in the system need to login again to see the effected changes.</p>
          </div>

          <div className="border-t pt-6 mt-6 flex justify-end">
            <Button onClick={saveSettings} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d4a6f] px-6">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </TabsContent>

        {/* TAB 2: Site For Modules */}
        <TabsContent value="modules" className="space-y-6">
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-2">Site For Modules</h2>
          </div>
          
          <p className="text-sm text-gray-700">Please select the modules for the sites.</p>

          <div className="grid grid-cols-2 gap-6 mt-6">
            {/* Separate Modules */}
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-6">Separate Modules</h3>
              <div className="border-t border-dashed pt-4">
                <div className="grid grid-cols-5 gap-4">
                  {SEPARATE_MODULES.map(mod => (
                    <ModuleIcon
                      key={mod.id}
                      module={mod}
                      selected={separateModules[mod.id]}
                      onClick={() => toggleSeparateModule(mod.id)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Interlinked Modules */}
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <h3 className="text-lg font-bold text-[#1e3a5f]">Interlinked Modules</h3>
                <div 
                  className={`relative w-16 h-8 rounded-full cursor-pointer transition-colors ${interlinkedEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  onClick={() => setInterlinkedEnabled(!interlinkedEnabled)}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform flex items-center justify-center text-xs font-bold ${interlinkedEnabled ? 'translate-x-9 text-green-600' : 'translate-x-1 text-gray-400'}`}>
                    {interlinkedEnabled ? 'Yes' : ''}
                  </div>
                </div>
              </div>
              <div className="border-t border-dashed pt-4">
                <div className="grid grid-cols-5 gap-4">
                  {INTERLINKED_MODULES.map(mod => (
                    <ModuleIcon
                      key={mod.id}
                      module={mod}
                      selected={interlinkedModules[mod.id]}
                      onClick={() => interlinkedEnabled && toggleInterlinkedModule(mod.id)}
                      disabled={!interlinkedEnabled}
                      interlinked
                    />
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-6 leading-relaxed">
                Please note: Category, Items, Supplier, Supplier Contract, Recipe, Inventory, Expcode4, Expcode5 and Expcode6 are all interlinked. You can either make them all Site based or none of them.
              </p>
            </div>
          </div>

          <div className="border-t pt-6 mt-6 flex justify-end">
            <Button onClick={saveSettings} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d4a6f] px-6">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </TabsContent>

        {/* TAB 3: Site Master */}
        <TabsContent value="master" className="space-y-6">
          <div className="border-b pb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-[#1e3a5f]">{siteName} Master</h2>
            <Button size="sm" onClick={openCreate} data-testid="add-site-btn" className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add {siteName}
            </Button>
          </div>

          {!enabled ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Site Function is not activated.</p>
              <p className="text-sm">Go to "Site Function" tab and activate it first.</p>
            </div>
          ) : sites.length === 0 ? (
            <EmptyState title={`No ${siteName.toLowerCase()}s defined`} description={`Add your first ${siteName.toLowerCase()} to begin siloing data.`} />
          ) : (
            <div className="space-y-2">
              {sites.map(s => (
                <div key={s.site_id} className="border rounded-lg overflow-hidden" data-testid={`site-row-${s.site_id}`}>
                  <div className="flex items-center gap-3 p-3 hover:bg-accent/20 transition-colors cursor-pointer" onClick={() => openEdit(s)}>
                    <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 text-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{s.name}</p>
                        {s.code && <Badge variant="outline" className="text-[10px]">{s.code}</Badge>}
                        {!s.active && <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Inactive</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{[s.city, s.state, s.country].filter(Boolean).join(', ') || 'No address'}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); toggleExpand(s.site_id); }} className="p-2 hover:bg-accent rounded flex items-center gap-1" title="Show users">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${expandedSite === s.site_id ? 'rotate-180' : ''}`} />
                    </button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteSite(s); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {expandedSite === s.site_id && (
                    <div className="border-t bg-muted/20 px-4 py-3">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Users at {s.name}</p>
                      {(siteUsers[s.site_id] || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground py-1">No users assigned to this {siteName.toLowerCase()}</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(siteUsers[s.site_id] || []).map(u => (
                            <Badge key={u.user_id} variant="outline" className="text-[10px]">
                              {u.first_name} {u.last_name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <Dialog open onOpenChange={() => setModalOpen(false)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isEdit ? `Edit ${siteName} — ${form.name}` : `New ${siteName}`}</DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details">{siteName} Details</TabsTrigger>
                {isEdit && <TabsTrigger value="users">Users ({(siteUsers[form.site_id] || []).length})</TabsTrigger>}
              </TabsList>
              <TabsContent value="details" className="space-y-4 pt-3">
                <SiteLogoUpload siteId={form.site_id} isEdit={isEdit} />
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label>{siteName} Name *</Label>
                    <Input value={form.name} onChange={e => f('name', e.target.value)} placeholder={`e.g. London Office`} data-testid="site-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{siteName} Code</Label>
                    <Input value={form.code} onChange={e => f('code', e.target.value.toUpperCase())} placeholder="e.g. LON" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={e => f('phone', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <AddressLookup
                      placeholder="Type address to search and auto-fill fields below..."
                      onSelect={({ address, city, state_province, zip_postal, country }) => {
                        if (address) f('address', address);
                        if (city) f('city', city);
                        if (state_province) f('state', state_province);
                        if (zip_postal) f('zip_postal', zip_postal);
                        if (country) f('country', country);
                      }}
                    />
                    <div className="grid grid-cols-2 gap-3 mt-1">
                      <div className="col-span-2 space-y-1"><Label className="text-xs">Street Address</Label><Input value={form.address} onChange={e => f('address', e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">City</Label><Input value={form.city} onChange={e => f('city', e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">State / Province</Label><Input value={form.state} onChange={e => f('state', e.target.value)} /></div>
                      <div className="space-y-1">
                        <Label className="text-xs">Zip / Postal Code</Label>
                        <Input value={form.zip_postal} onChange={e => f('zip_postal', e.target.value)} />
                        {form.country === 'United Kingdom' && (
                          <div className="mt-1">
                            <PostcodeLookup onFound={({ zip_postal, city, state_province }) => {
                              f('zip_postal', zip_postal);
                              if (city) f('city', city);
                              if (state_province) f('state', state_province);
                            }} />
                          </div>
                        )}
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Country</Label>
                        <select value={form.country} onChange={e => f('country', e.target.value)}
                          className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                          {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={e => f('email', e.target.value)} />
                  </div>
                  <div className="flex items-center gap-3 pt-4">
                    <Label>Active</Label>
                    <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
                  </div>
                </div>
              </TabsContent>
              {isEdit && (
                <TabsContent value="users" className="pt-3">
                  <p className="text-xs text-muted-foreground mb-3">Users assigned to this {siteName.toLowerCase()}. Manage user assignments from User Maintenance.</p>
                  {(siteUsers[form.site_id] || []).length === 0 ? (
                    <EmptyState title="No users" description={`Assign users to this ${siteName.toLowerCase()} from the User Edit screen.`} />
                  ) : (
                    <div className="border rounded-lg divide-y">
                      {(siteUsers[form.site_id] || []).map(u => (
                        <div key={u.user_id} className="flex items-center gap-3 px-3 py-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">
                            {(u.first_name?.[0] || '')}{(u.last_name?.[0] || '')}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-muted-foreground">{u.email} &middot; {u.user_type || 'User'}</p>
                          </div>
                          {u.is_admin && <Badge className="text-[10px]">Admin</Badge>}
                          {!u.active && <Badge variant="outline" className="text-[10px] text-red-600">Inactive</Badge>}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={saveSite} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">
                {saving ? 'Saving...' : `Save ${siteName}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function ModuleIcon({ module, selected, onClick, disabled, interlinked }) {
  const Icon = module.icon;
  const baseColor = interlinked ? '#1e3a5f' : '#6b7280';
  
  return (
    <div 
      className={`flex flex-col items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={disabled ? undefined : onClick}
    >
      <div className="relative">
        {/* Outer ring */}
        <div 
          className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-colors ${
            selected 
              ? (interlinked ? 'border-[#1e3a5f] bg-[#1e3a5f]/10' : 'border-[#1e3a5f] bg-[#1e3a5f]/10')
              : 'border-gray-200 bg-gray-50'
          }`}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selected ? 'bg-white' : 'bg-gray-100'}`}>
            <Icon className={`h-5 w-5 ${selected ? 'text-[#1e3a5f]' : 'text-gray-400'}`} />
          </div>
        </div>
        {/* Check badge */}
        {selected && (
          <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${interlinked ? 'bg-orange-500' : 'bg-green-500'}`}>
            <Check className="h-3 w-3 text-white" />
          </div>
        )}
      </div>
      <span className={`text-xs mt-2 text-center leading-tight ${selected ? 'text-[#1e3a5f] font-medium' : 'text-gray-500'}`}>
        {module.name}
      </span>
    </div>
  );
}


function SiteLogoUpload({ siteId, isEdit }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEdit || !siteId) return;
    api.get(`/api/sites/${siteId}/logo`, { responseType: 'blob' }).then(r => {
      if (r.data) setLogoUrl(URL.createObjectURL(r.data));
    }).catch(() => {});
  }, [siteId, isEdit]);

  const upload = async (file) => {
    if (!file || !siteId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/api/sites/${siteId}/logo`, fd);
      const r = await api.get(`/api/sites/${siteId}/logo`, { responseType: 'blob' });
      setLogoUrl(URL.createObjectURL(r.data));
      toast.success('Logo uploaded');
    } catch { toast.error('Logo upload failed'); }
  };

  if (!isEdit) return null;

  return (
    <div className="border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer hover:border-primary/40"
      style={dragging ? { borderColor: 'hsl(var(--primary))', background: 'hsl(var(--primary) / 0.05)' } : {}}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files[0]); }}
      onClick={() => inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files[0])} />
      {logoUrl ? (
        <img src={logoUrl} alt="Site logo" className="h-12 mx-auto object-contain" />
      ) : (
        <div className="py-2">
          <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
          <p className="text-xs text-muted-foreground">Drop logo here or click to upload</p>
        </div>
      )}
    </div>
  );
}
